import { createSelector } from 'reselect';
import { TDatas } from '../../models/types/Datas';
import { EConsoData } from '../../models/entities/datas/consoData';
import { ELocalData } from '../../models/entities/datas/localData';
import { selectCurrentParticipant } from 'src/module/participant/participant.slice';

const datas = (state: { datas: TDatas }) => state.datas;

// myDatas
const selectSelectedDatasInterval = createSelector(datas, (datas) => datas && datas.interval);
const selectDateFrom = createSelector(datas, (datas) => datas && datas.dateFrom);
const selectDateTo = createSelector(datas, (datas) => datas && datas.dateTo);
const selectSelectedDatasCurrentDate = createSelector(datas, (datas) => datas && datas.currentDate);

const selectSelectedDatasConsoDataUnformatted = createSelector(datas, (datas) => datas && datas.consoData);
const selectSelectedDatasConsoData = createSelector(selectSelectedDatasConsoDataUnformatted, (consoData) =>
  consoData.map((consoData: EConsoData) => {
    consoData.power = consoData.power - consoData.powerACC;
    return consoData;
  }),
);

const selectLocalDataByParticipant = createSelector(datas, (datas) => datas && datas.localData);
const selectParticipantView = createSelector(datas, (datas) => datas?.participantView ?? 'GLOBAL');

function aggregateLocalData(allData: ELocalData[][]): ELocalData[] {
  if (allData.length === 0) return [];
  const first = allData.find((d) => d.length >= 3);
  if (!first) return [];
  // [0] myShare: sum across participants
  const totalMyShare = allData.reduce((sum, d) => sum + (d[0]?._value || 0), 0);
  // global = myShare + othersShare for any participant (operation-level value, identical for all)
  const globalValue = (first[0]?._value || 0) + (first[1]?._value || 0);
  // [1] othersShare = global - all myShares
  const totalOthersShare = globalValue - totalMyShare;
  // [2] rejectedShare: operation-level surplus, same for all participants
  const rejectedShare = first[2]?._value || 0;
  return [
    new ELocalData(first[0]._name, totalMyShare),
    new ELocalData(first[1]._name, totalOthersShare),
    new ELocalData(first[2]._name, rejectedShare),
  ];
}

const selectSelectedDatasLocalData = createSelector(
  [selectLocalDataByParticipant, selectParticipantView],
  (localDataByParticipant, participantView) => {
    if (!localDataByParticipant || localDataByParticipant.length === 0) return [];
    if (participantView !== 'GLOBAL') {
      const id = Number(participantView);
      return localDataByParticipant.find((e) => e.participantId === id)?.data ?? [];
    }
    return aggregateLocalData(localDataByParticipant.map((e) => e.data));
  },
);

const selectSelectedDatasLocalDataIsReal = createSelector(datas, (datas) => datas && datas.localDataIsReal);
const selectSelectedDatasConsoDataIsReal = createSelector(datas, (datas) => datas && datas.consoDataIsReal);
const selectUploadedDateRange = createSelector(datas, (datas) => datas && datas.uploadedDateRange);
const selectUploadedDateRangeFrom = createSelector(
  selectUploadedDateRange,
  (uploadedDateRange) => uploadedDateRange && uploadedDateRange.from,
);
const selectUploadedDateRangeTo = createSelector(
  selectUploadedDateRange,
  (uploadedDateRange) => uploadedDateRange && uploadedDateRange.to,
);
const selectOperationConsoData = createSelector(
  [selectSelectedDatasConsoData, selectCurrentParticipant],
  (consoData, currentParticipant) => ({
    data: consoData,
    isGlobalView: !currentParticipant,
  }),
);
const selectIsDataLoading = createSelector(datas, (datas) => datas && datas.isFetching);
export {
  selectUploadedDateRangeFrom,
  selectUploadedDateRangeTo,
  selectSelectedDatasInterval,
  selectSelectedDatasConsoData,
  selectSelectedDatasLocalData,
  selectSelectedDatasCurrentDate,
  selectSelectedDatasLocalDataIsReal,
  selectDateFrom,
  selectDateTo,
  selectSelectedDatasConsoDataIsReal,
  selectUploadedDateRange,
  selectOperationConsoData,
  selectIsDataLoading,
  selectParticipantView,
};
